// Onboarding Context - Manages company profile completion flow
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_API_URL || 'https://api.xpherium.com/api';
const ONBOARDING_REQUEST_TIMEOUT_MS = 7000;

export const OnboardingProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [profileComplete, setProfileComplete] = useState(false);
  const [hasDataIntegration, setHasDataIntegration] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companyProfile, setCompanyProfile] = useState(null);

  // Check user's onboarding status when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      checkOnboardingStatus();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const checkOnboardingStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Check if company profile exists
      const profileResponse = await axios.get(`${API_URL}/onboarding/profile`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: ONBOARDING_REQUEST_TIMEOUT_MS
      });

      if (profileResponse.data.success && profileResponse.data.profile) {
        // Profile is only complete if it has completedAt timestamp
        const isComplete = !!profileResponse.data.profile.completedAt;
        setProfileComplete(isComplete);
        setCompanyProfile(profileResponse.data.profile);
        
        // Store in localStorage for quick access
        localStorage.setItem('companyProfile', JSON.stringify(profileResponse.data.profile));
        console.log('📋 Profile status:', isComplete ? 'Complete' : 'Incomplete (no completedAt)');
      } else {
        setProfileComplete(false);
        console.log('📋 Profile status: No profile found');
      }

      // Check if user has any data integrations
      const dataResponse = await axios.get(`${API_URL}/onboarding/data-status`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: ONBOARDING_REQUEST_TIMEOUT_MS
      });

      setHasDataIntegration(dataResponse.data.hasData || false);

    } catch (error) {
      console.error('Error checking onboarding status:', error);
      
      // If profile endpoint doesn't exist, assume not complete
      setProfileComplete(false);
      setHasDataIntegration(false);
    } finally {
      setLoading(false);
    }
  };

  const saveCompanyProfile = async (profileData) => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔵 OnboardingContext: Saving company profile...', profileData);
      console.log('🔵 Token:', token ? 'Present' : 'Missing');
      
      const response = await axios.post(
        `${API_URL}/onboarding/profile`,
        profileData,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: ONBOARDING_REQUEST_TIMEOUT_MS
        }
      );

      console.log('🔵 Response:', response.data);

      if (response.data.success) {
        setProfileComplete(true);
        setCompanyProfile(response.data.profile);
        localStorage.setItem('companyProfile', JSON.stringify(response.data.profile));
        // Keep Competitor Intelligence cache aligned immediately after onboarding save.
        try {
          const ciRaw = localStorage.getItem('ci_state_v1');
          if (ciRaw) {
            const ciState = JSON.parse(ciRaw);
            localStorage.setItem('ci_state_v1', JSON.stringify({
              ...ciState,
              profileData: {
                ...(ciState?.profileData || {}),
                ...response.data.profile,
              },
            }));
          }
        } catch {
          // Non-blocking cache sync safeguard
        }
        console.log('✅ Profile saved successfully!');
        return { success: true };
      }

      console.log('⚠️ Profile save failed:', response.data);
      return { success: false, error: 'Failed to save profile' };
    } catch (error) {
      console.error('❌ Error saving company profile:', error);
      console.error('❌ Error response:', error.response?.data);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to save profile' 
      };
    }
  };

  const refreshDataStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/onboarding/data-status`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: ONBOARDING_REQUEST_TIMEOUT_MS
      });
      
      setHasDataIntegration(response.data.hasData || false);
      return response.data.hasData;
    } catch (error) {
      console.error('Error refreshing data status:', error);
      return false;
    }
  };

  const value = {
    profileComplete,
    hasDataIntegration,
    loading,
    companyProfile,
    saveCompanyProfile,
    refreshDataStatus,
    checkOnboardingStatus
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

